/**
 * StaminaBar - Displays player's current stamina for sprinting
 */

import React from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../state/store';

interface StaminaBarProps {
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

export const StaminaBar: React.FC<StaminaBarProps> = ({
  position = 'top-left'
}) => {
  const stamina = useSelector((state: RootState) => state.player.stamina);
  const maxStamina = useSelector((state: RootState) => state.player.maxStamina);

  const staminaPercentage = (stamina / maxStamina) * 100;

  // Position styling
  const positionStyles = {
    'top-left': { top: '90px', left: '20px' },
    'top-right': { top: '90px', right: '20px' },
    'bottom-left': { bottom: '20px', left: '20px' },
    'bottom-right': { bottom: '20px', right: '20px' },
  };

  // Color based on stamina level
  const getStaminaColor = () => {
    if (staminaPercentage > 60) return '#00ff00'; // Green
    if (staminaPercentage > 30) return '#ffaa00'; // Orange
    return '#ff0000'; // Red
  };

  return (
    <div style={{
      position: 'absolute',
      ...positionStyles[position],
      pointerEvents: 'none',
      fontFamily: 'monospace',
    }}>
      {/* Label */}
      <div style={{
        fontSize: '12px',
        color: '#aaa',
        marginBottom: '5px',
        textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
      }}>
        STAMINA
      </div>

      {/* Stamina Bar Container */}
      <div style={{
        width: '200px',
        height: '24px',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        border: '2px solid #444',
        borderRadius: '4px',
        overflow: 'hidden',
        position: 'relative',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
      }}>
        {/* Stamina Fill */}
        <div style={{
          width: `${staminaPercentage}%`,
          height: '100%',
          backgroundColor: getStaminaColor(),
          transition: 'width 0.3s ease, background-color 0.3s ease',
          boxShadow: `0 0 10px ${getStaminaColor()}80`,
        }}>
          {/* Shine effect */}
          <div style={{
            width: '100%',
            height: '50%',
            background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.3), transparent)',
          }} />
        </div>

        {/* Stamina Text */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: '12px',
          fontWeight: 'bold',
          color: '#ffffff',
          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.9)',
          pointerEvents: 'none',
        }}>
          {Math.round(stamina)} / {maxStamina}
        </div>

        {/* Low stamina warning pulse */}
        {staminaPercentage < 20 && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 0, 0, 0.3)',
            animation: 'pulse 1s infinite',
          }} />
        )}
      </div>

      {/* Low stamina warning text */}
      {staminaPercentage < 20 && (
        <div style={{
          marginTop: '5px',
          fontSize: '10px',
          color: '#ff0000',
          fontWeight: 'bold',
          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
          animation: 'pulse 1s infinite',
        }}>
          ⚠️ LOW STAMINA
        </div>
      )}

      {/* Keyframes for animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.4;
          }
        }
      `}</style>
    </div>
  );
};
